import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { Card, EmptyState, Spin, Typography } from '../../ui';
import { request } from '../../api/client';
import { inr2 as inr } from '../../lib/format';

/**
 * The retailer's own commercial terms, read-only.
 *
 * The workflow is: staff create a scheme and assign retailers to it. The retailer takes no action — there
 * is no acceptance step — but until now they could not even READ the terms they were put on: RETAILER
 * holds no COMMERCIAL.* permission, so the staff screen is closed to them and the only way to learn
 * your own discount rates was to ask your rep. This page is purely that reference, showing exactly the
 * terms the server says apply to the signed-in retailer today and revealing nothing about any other.
 */
const pct = (v: any) => `${Number(v ?? 0)}%`;

function Slabs({ title, blurb, head, rows, empty }: { title: string; blurb: string; head: string[]; rows: any[][]; empty: string }) {
  return (
    <Card title={title} density="compact" className="h-full">
      <p className="type-caption mb-2 text-muted">{blurb}</p>
      {rows.length === 0 ? (
        <p className="type-caption text-subtle">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="gb-thead-tint">
              <tr className="border-b border-line text-left">
                {head.map((h) => <th key={h} className="py-1.5 pr-3 type-label text-muted">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-line/60 last:border-0">
                  {r.map((c, j) => <td key={j} className="py-1.5 pr-3 tabular-nums text-content">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function MyTermsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-commercial-terms'],
    queryFn: () => request<any>({ url: '/discount-schemes/my-terms' }),
  });

  if (isLoading) return <div className="grid place-items-center py-24"><Spin /></div>;

  const scheme = data?.scheme;

  // Being on no scheme is an ordinary state, not an error — say so plainly rather than show empty tables.
  if (!scheme) {
    return (
      <div className="p-1">
        <h1 className="type-page-title text-content">My commercial terms</h1>
        <Card className="mt-4">
          <EmptyState
            icon={<ScrollText />}
            title="No commercial scheme is currently assigned to you"
            description="Cash discounts, credit terms and the year-end turnover discount apply only to retailers on a live scheme. Your sales representative can tell you more."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-1">
      {/* No acceptance step is given to retailers — a scheme is created and retailers are assigned to it, and
          that is the whole workflow. So no "Assigned / Accepted" badge here: it would imply an action the
          retailer is waiting to take, or has taken, when neither is true. */}
      <h1 className="type-page-title text-content">My commercial terms</h1>
      <p className="type-body mt-1 text-muted">
        The discount and credit terms that apply to your purchases. Set by Green Bay — shown here for your reference.
      </p>

      <Card className="mt-4" title={scheme.name}
        extra={<span className="type-caption text-muted">FY {scheme.financialYear}</span>}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <div>
            <div className="type-label text-muted">Credit period</div>
            <div className="type-metric-sm text-content">{scheme.creditPeriodDays} days</div>
            <div className="type-caption text-subtle">to pay before interest starts</div>
          </div>
          <div>
            <div className="type-label text-muted">Overdue interest</div>
            <div className="type-metric-sm text-content">{pct(scheme.overdueInterestPct)}<span className="type-caption text-subtle"> / month</span></div>
            <div className="type-caption text-subtle">on anything unpaid after that</div>
          </div>
          <div>
            <div className="type-label text-muted">In effect from</div>
            <div className="type-metric-sm text-content">{scheme.effectiveFrom}</div>
            <div className="type-caption text-subtle">{scheme.effectiveTo ? `until ${scheme.effectiveTo}` : 'no end date'}</div>
          </div>
        </div>
      </Card>

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Slabs
          title="Pay before dispatch" blurb="Pay in full up front and the order is discounted by the amount you prepay."
          head={['You prepay at least', 'You save']}
          rows={(scheme.advanceCdSlabs ?? []).map((s: any) => [inr(s.minAmount), pct(s.cdPct)])}
          empty="No prepayment discount on this scheme."
        />
        <Slabs
          title="Pay quickly after delivery" blurb="Settle the invoice within these windows and the discount still applies. Day 0 means paying at billing."
          head={['Paid within', 'You save']}
          rows={(scheme.timelineCdSlabs ?? []).map((s: any) => [s.maxDays === 0 ? 'At billing' : `${s.maxDays} days`, pct(s.cdPct)])}
          empty="No prompt-payment discount on this scheme."
        />
      </div>

      <div className="mt-3">
        <Slabs
          title="Year-end turnover discount" blurb="Worked out on your purchases across the whole financial year, and issued as a credit note after year end."
          head={['Yearly purchases from', 'Base', 'Per completed lakh above', 'Maximum']}
          rows={(scheme.todSlabs ?? []).map((s: any) => [inr(s.fromAmount), pct(s.basePct), pct(s.perLakhPct), pct(s.capPct)])}
          empty="No turnover discount on this scheme."
        />
      </div>

      {scheme.notes && (
        <Card className="mt-3" title="Notes" density="compact">
          <Typography.Paragraph className="type-body mb-0 text-muted">{scheme.notes}</Typography.Paragraph>
        </Card>
      )}

      <p className="type-caption mt-3 text-subtle">
        Discounts are applied by Green Bay when your order is billed or your payment is recorded. If anything here
        looks wrong, please contact your sales representative.
      </p>
    </div>
  );
}
